import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const TIPO_LABEL = { admin_asistentas: "Admin Asistentas 🧹", admin_atcliente: "Admin At.Cliente 🤝", admin_servicios: "Admin Servicios 🏠" };
const TIPO_COLOR = { admin_asistentas: "#ea580c", admin_atcliente: "#7c3aed", admin_servicios: "#059669" };

const SIN_ENCARGADOS = "__sin_encargados__";

const AdminGestionEncargados = () => {
  const navigate = useNavigate();

  const [supervisoras, setSupervisoras] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busqueda, setBusqueda] = useState("");
  // "" = todos, SIN_ENCARGADOS = supervisoras sin ninguno, o el user del encargado
  const [filtroEncargado, setFiltroEncargado] = useState("");
  const [accionando, setAccionando] = useState(null); // `${supervisora}:${admin}` en curso

  const cargar = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get("/api/usuarios/supervisoras-encargadas");
      setSupervisoras(res.data?.supervisoras || []);
      setAdmins(res.data?.admins || []);
    } catch (e) {
      setError(e.response?.data?.error || "Error al cargar los datos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const asignar = async (supervisoraUser, adminUser) => {
    if (!adminUser) return;
    const clave = `${supervisoraUser}:${adminUser}`;
    setAccionando(clave);
    setError("");
    try {
      await axios.post(`/api/usuarios/encargadas/${adminUser}`, { supervisora_user: supervisoraUser });
      await cargar();
    } catch (e) {
      setError(e.response?.data?.error || "Error al asignar.");
    } finally {
      setAccionando(null);
    }
  };

  const quitar = async (supervisoraUser, adminUser) => {
    const clave = `${supervisoraUser}:${adminUser}`;
    setAccionando(clave);
    setError("");
    try {
      await axios.delete(`/api/usuarios/encargadas/${adminUser}/${supervisoraUser}`);
      await cargar();
    } catch (e) {
      setError(e.response?.data?.error || "Error al quitar.");
    } finally {
      setAccionando(null);
    }
  };

  const supervisorasFiltradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return supervisoras.filter((s) => {
      if (q && !(s.user.toLowerCase().includes(q) || (s.nombre || "").toLowerCase().includes(q))) return false;
      if (filtroEncargado === SIN_ENCARGADOS) return s.encargados.length === 0;
      if (filtroEncargado) return s.encargados.some((e) => e.user === filtroEncargado);
      return true;
    });
  }, [supervisoras, busqueda, filtroEncargado]);

  return (
    <div style={s.page}>
      <div style={s.header}>
        <button onClick={() => navigate(-1)} style={s.btnVolver}>← Volver</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, color: "rgba(255,255,255,0.55)" }}>Gestionar datos</div>
          <strong style={{ fontSize: 24, color: "#fff" }}>🗂️ Encargados de supervisoras</strong>
        </div>
        <div style={s.totalBadge}>
          <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1 }}>{supervisoras.length}</div>
          <div style={{ fontSize: 11, opacity: 0.75 }}>supervisora{supervisoras.length !== 1 ? "s" : ""}</div>
        </div>
      </div>

      {error && <div style={s.errorBox}>{error}</div>}

      <div style={s.content}>
        <div style={s.filtros}>
          <input
            type="text"
            placeholder="Buscar supervisora…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            style={{ ...s.buscador, flex: "2 1 220px", marginBottom: 0 }}
          />
          <select
            value={filtroEncargado}
            onChange={(e) => setFiltroEncargado(e.target.value)}
            style={{ ...s.buscador, flex: "1 1 200px", marginBottom: 0, background: "#fff", cursor: "pointer" }}
          >
            <option value="">Todos los encargados</option>
            <option value={SIN_ENCARGADOS}>Sin encargados</option>
            {admins.map((a) => (
              <option key={a.user} value={a.user}>
                {(a.nombre || a.user)} — {TIPO_LABEL[a.tipo] || a.tipo}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <p style={{ textAlign: "center", color: "#64748b", padding: 40, fontSize: 16 }}>Cargando…</p>
        ) : supervisorasFiltradas.length === 0 ? (
          <div style={s.vacio}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>🗂️</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#475569" }}>
              {busqueda || filtroEncargado ? "Sin resultados." : "No hay supervisoras activas."}
            </div>
          </div>
        ) : (
          <div style={s.grid}>
            {supervisorasFiltradas.map((sup) => {
              const encargadosUsers = new Set(sup.encargados.map((e) => e.user));
              const disponibles = admins.filter((a) => !encargadosUsers.has(a.user));
              const tieneEncargados = sup.encargados.length > 0;
              return (
                <div
                  key={sup.user}
                  style={{
                    ...s.card,
                    background: tieneEncargados ? "#f0fdf4" : "#fef2f2",
                    border: tieneEncargados ? "1.5px solid #86efac" : "1.5px solid #fca5a5",
                  }}
                >
                  <div style={s.cardTop}>
                    <div style={s.avatar}>{(sup.nombre || sup.user)[0].toUpperCase()}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={s.nombreSup}>{sup.nombre ? `${sup.nombre} ${sup.apellidos || ""}`.trim() : sup.user}</div>
                      {sup.nombre && <div style={s.userSup}>@{sup.user}</div>}
                    </div>
                  </div>

                  <div style={s.chips}>
                    {sup.encargados.length === 0 ? (
                      <span style={s.sinEncargados}>Sin encargados asignados</span>
                    ) : (
                      sup.encargados.map((enc) => {
                        const clave = `${sup.user}:${enc.user}`;
                        const color = TIPO_COLOR[enc.tipo] || "#374151";
                        return (
                          <span key={enc.user} style={{ ...s.chip, background: `${color}1a`, border: `1.5px solid ${color}`, color }}>
                            {enc.nombre || enc.user}
                            <span style={{ opacity: 0.75, fontWeight: 600 }}> · {TIPO_LABEL[enc.tipo] || enc.tipo}</span>
                            <button
                              onClick={() => quitar(sup.user, enc.user)}
                              disabled={accionando === clave}
                              style={{ ...s.btnQuitarChip, color }}
                              title="Quitar encargado"
                            >
                              {accionando === clave ? "…" : "✕"}
                            </button>
                          </span>
                        );
                      })
                    )}
                  </div>

                  {disponibles.length > 0 && (
                    <select
                      value=""
                      onChange={(e) => asignar(sup.user, e.target.value)}
                      style={s.select}
                    >
                      <option value="">+ Añadir encargado…</option>
                      {disponibles.map((a) => (
                        <option key={a.user} value={a.user}>
                          {(a.nombre || a.user)} — {TIPO_LABEL[a.tipo] || a.tipo}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const s = {
  page: { minHeight: "100vh", background: "#f0f4f8", fontFamily: "Segoe UI, sans-serif" },
  header: { background: "linear-gradient(135deg,#1e293b,#0f172a)", color: "#fff", padding: "20px 24px", display: "flex", alignItems: "center", gap: 16 },
  btnVolver: { background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 10, color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer", padding: "10px 16px", whiteSpace: "nowrap" },
  totalBadge: { textAlign: "center", background: "rgba(255,255,255,0.1)", borderRadius: 14, padding: "8px 18px", color: "#fff" },
  errorBox: { background: "#fee2e2", color: "#b91c1c", borderRadius: 10, padding: "12px 18px", margin: "18px 24px 0", fontSize: 14 },
  content: { padding: 24, maxWidth: 780, margin: "0 auto" },
  filtros: { display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 20 },
  buscador: { width: "100%", padding: "12px 16px", borderRadius: 12, border: "1.5px solid #e2e8f0", fontSize: 15, marginBottom: 20, boxSizing: "border-box", outline: "none" },

  vacio: { background: "#fff", borderRadius: 18, boxShadow: "0 2px 10px rgba(15,23,42,0.06)", padding: "40px 20px", textAlign: "center" },

  grid: { display: "flex", flexDirection: "column", gap: 14 },
  card: { background: "#fff", borderRadius: 18, boxShadow: "0 2px 12px rgba(15,23,42,0.07)", border: "1.5px solid #eef2f7", padding: "18px 20px" },
  cardTop: { display: "flex", alignItems: "center", gap: 14, marginBottom: 14 },
  avatar: { width: 48, height: 48, borderRadius: "50%", background: "linear-gradient(135deg,#c084fc,#a855f7)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 19, flexShrink: 0 },
  nombreSup: { fontWeight: 800, fontSize: 17, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  userSup: { fontSize: 13, color: "#94a3b8", marginTop: 1 },

  chips: { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 },
  sinEncargados: { fontSize: 13.5, color: "#94a3b8", fontStyle: "italic" },
  chip: { display: "inline-flex", alignItems: "center", gap: 6, borderRadius: 999, padding: "6px 8px 6px 14px", fontSize: 13.5, fontWeight: 700 },
  btnQuitarChip: { background: "none", border: "none", cursor: "pointer", fontWeight: 800, fontSize: 13, padding: "2px 6px", borderRadius: 999 },

  select: { width: "100%", padding: "10px 12px", borderRadius: 10, border: "1.5px solid #e2e8f0", fontSize: 14, background: "#f8fafc", color: "#334155", cursor: "pointer", boxSizing: "border-box" },
};

export default AdminGestionEncargados;
